import { HEADERS_REEMA_IMPERSONATION_USER_ID } from '@act/shared/constants';
import { SerializedError } from '@reduxjs/toolkit';
import { ValidationError } from 'class-validator';
import { stringify as stringifyQueryString } from 'querystring';
import { getEnvironment } from 'toolkit/environment/environment/environment';
import { ApiRequestDefaults, ApiRequestParams } from './api-request-params';
import { EndpointDefinition } from './endpoint-definition';
import { getToken } from './firebase-auth';

type Method = 'POST' | 'PATCH' | 'PUT' | 'GET' | 'DELETE';
export interface ApiRequestError extends SerializedError {
  errors: ValidationError[];
  body: object;
  isAccessDenied?: boolean;
}

interface Pagination {
  pageSize: number;
  pageIndex: number;
}

export const makeRequest = async <
  RESPONSE extends object | void,
  BODY extends object,
  PARAMS extends ApiRequestParams,
>(
  endpoint: EndpointDefinition<RESPONSE, BODY, PARAMS>,
  {
    params,
    body,
  }: {
    params?: PARAMS;
    body?: BODY;
    defaults?: ApiRequestDefaults;
  }
): Promise<RESPONSE> => {
  const url = buildUrl(endpoint.route, params, null, endpoint.defaults);
  const token = await getToken();
  const impersonatedUserId = localStorage.getItem('impersonatedUserId');
  const language = localStorage.getItem('language');

  const response = await fetch(url, {
    method: endpoint.method,
    headers: {
      ...(impersonatedUserId && {
        [HEADERS_REEMA_IMPERSONATION_USER_ID]: impersonatedUserId,
      }),
      ...(language && {
        ['x-reema-language']: language,
      }),
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': `application/json`,
    },
    body: body === null ? null : JSON.stringify(body),
  });

  if (!response.ok) {
    const finalizedResponse = await response.json();
    const formValidationError = finalizedResponse.errors;
    const isAccessDenied = response.headers.get('X-Access-Denied') === 'true';
    const serializedError: ApiRequestError = {
      name: 'HttpError',
      message: `${response.status} error for route ${response.url}`,
      code: response.status.toString(),
      body: finalizedResponse,
      errors: formValidationError,
      isAccessDenied,
    };
    throw serializedError;
  }

  const responseText = await response.text();

  if (responseText === '') {
    return undefined as RESPONSE;
  }

  const responseBody = JSON.parse(responseText) as RESPONSE;
  return responseBody;
};

const httpEscape = (str: string) =>
  stringifyQueryString({ test: str }).replace('test=', '');

const buildUrl = (
  url: string,
  params: ApiRequestParams,
  pagination: Pagination,
  defaults?: ApiRequestDefaults
): string => {
  let queryParamKeys: string[] = [];
  let routeParamKeys: string[] = [];
  const combinedParams = { ...params };

  if (params || defaults?.params) {
    if (defaults?.params) {
      Object.entries(defaults.params).forEach(([key, value]) => {
        if (combinedParams[key] === undefined) {
          combinedParams[key] = value;
        }
      });
    }

    const routeParamMatch = url.match(/\/:(\w|\d|-)+/g);
    if (routeParamMatch) {
      routeParamKeys = routeParamMatch
        .map(m => m.replace('/:', ''))
        .filter(key => params[key] !== undefined);
    }
    queryParamKeys = Object.entries(combinedParams)
      .filter(([key, value]) => !routeParamKeys.find(k => k === key) && value)
      .map(([key]) => key);
  }

  if (routeParamKeys) {
    url = routeParamKeys.reduce((url: string, key): string => {
      const value = combinedParams[key];
      if (value != null) {
        return url.replace(`:${key}`, httpEscape(value.toString()));
      }
    }, url);
  }

  if (queryParamKeys || pagination) {
    const query: ApiRequestParams = queryParamKeys.reduce(
      (queryParams, key) => {
        queryParams[key] = combinedParams[key];
        return queryParams;
      },
      {}
    );

    if (pagination) {
      query['page-start'] = pagination.pageIndex * pagination.pageSize;
      query['page-size'] = pagination.pageSize;
    }

    // Array to comma delimited string
    Object.entries(query).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        query[key] = value.join(',');
      }
    });
    const qs = stringifyQueryString(query);
    if (qs && qs != '') {
      url = `${url}?${qs}`;
    }
  }

  const baseUrl = getEnvironment().serviceUris.websocketService;
  return baseUrl + url;
};
